import { checkIsLineOfCreditOrMortgageTypeOfCredit } from 'app/pages/loanApplication/LoanApplication.utils';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { LiabilitiesType } from 'constants/loanApplication';
import { liabilityLinkedToPropertyGroupedOptions } from 'constants/options';
import { getLiabilityOption, getPersonFullName } from 'utils/loanApplication';

import { selectBorrowers } from './borrowers.selectors';
import { selectLoanApplication } from './loanApplication.selectors';
import { selectPropertyLinkedLiabilityIds } from './property.selectors';

const selectLiabilities = createSelector(selectLoanApplication, ({ liabilities }) => liabilities);

export const selectLiabilitiesValues = createSelector(selectLiabilities, liabilities =>
  _.orderBy(
    Object.values(liabilities),
    liability => _.get(liability, 'unpaidBalance') || 0,
    'desc',
  ),
);

const selectExtendedLiabilities = createSelector(
  selectLiabilitiesValues,
  selectBorrowers,
  (liabilities, borrowers) => {
    return liabilities?.reduce((acc, liability) => {
      const {
        accountType,
        property,
        owners,
        type: liabilityType,
        liabilityExpenseType,
      } = liability || {};
      const { isSubjectProperty, propertyId, status } = property || {};
      const isLineOfCreditOrMortgageCreditType =
        checkIsLineOfCreditOrMortgageTypeOfCredit(accountType);
      const isOtherLiabilityType = liabilityType === LiabilitiesType.OTHER;

      const ownerNamesById = owners?.reduce((acc, ownerId) => {
        const relatedBorrower = borrowers[ownerId];
        const fullName = getPersonFullName(relatedBorrower);

        acc[ownerId] = fullName;

        return acc;
      }, {});

      const getLiabilityLinkedToPropertyGroup = () => {
        if (!isLineOfCreditOrMortgageCreditType || !isSubjectProperty) {
          return null;
        }

        const checkedOptions = liabilityLinkedToPropertyGroupedOptions
          .filter(({ value: nameInModel }) => liability[nameInModel])
          .map(({ value: nameInModel }) => nameInModel);

        return checkedOptions.length === 1 ? checkedOptions[0] : null;
      };

      const liabilityLinkedToPropertyGroup = getLiabilityLinkedToPropertyGroup();

      acc.push({
        ownerNamesById,
        ownerName: ownerNamesById ? Object.values(ownerNamesById).join(' / ') : '',
        propertyId,
        propertyStatus: status,
        liabilityLinkedToPropertyGroup,
        accountTypeOrLiabilityExpenseType: isOtherLiabilityType
          ? liabilityExpenseType
          : accountType,
        ...liability,
      });

      return acc;
    }, []);
  },
);

export const selectMortgageLiabilityOptions = memoizeOne(
  (firstMortgage, secondMortgage) =>
    createSelector(
      [selectExtendedLiabilities, selectPropertyLinkedLiabilityIds],
      (liabilities, usedLiabilitiesIds) => {
        const filteredLiabilities = liabilities.filter(
          ({ accountType, liabilityId }) =>
            checkIsLineOfCreditOrMortgageTypeOfCredit(accountType) &&
            !usedLiabilitiesIds.includes(liabilityId),
        );

        return [firstMortgage, secondMortgage]
          .filter(Boolean)
          .concat(filteredLiabilities)
          .map(getLiabilityOption);
      },
    ),
  _.isEqual,
);
